import {Component, ElementRef, OnInit, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Button, ButtonDirective} from 'primeng/button';
import {
  JobRunStatus,
  JobStatusDetails,
  SystemAdminService
} from '../../../api';
import {LoadingService} from '../../../services/loading.service';
import {formatDuration} from '../../../util/date.utils';
import {TableModule} from 'primeng/table';
import {TaAppStore} from '../../../state/clcta-store';
import {Card} from 'primeng/card';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {PermissionService} from '../../../services/permission.service';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {Fieldset} from 'primeng/fieldset';
import {RouterLink} from '@angular/router';
import {Tooltip} from 'primeng/tooltip';
import {Tag} from 'primeng/tag';

@Component({
  selector: 'system-admin',
  imports: [
    Button,
    TableModule,
    Card,
    FormsModule,
    InputText,
    ToggleSwitch,
    Fieldset,
    RouterLink,
    ButtonDirective,
    Tooltip,
    Tag,
    DatePipe
  ],
  templateUrl: './clc-system-admin.html',
  styleUrl: './clc-system-admin.scss',
  providers: [DynamicDialogRef, DialogService],
})
export class ClcSystemAdmin implements OnInit {
  emailSettings = signal<{ [key: string]: string | null; }>({'Email_Smtp_Host': null, 'Email_FromAddress': null, 'Email_Enabled': null, 'Email_ComplianceAddress': null});
  emailEnabled = signal(false);

  jobStatusList = signal<JobStatusDetails[]>([]);

  isSystemAdmin: boolean = false;

  constructor(
    private store: TaAppStore,
    private systemApi: SystemAdminService,
    private host: ElementRef,
    private loadingService: LoadingService,
    private permissionService: PermissionService,
  ) {
    this.isSystemAdmin = this.permissionService.isSystemAdmin();
  }

  ngOnInit(): void {

    this.loadingService.showLoading({message: 'Loading...', targetElement: this.host});

    this.systemApi.getEmailSettings().subscribe({
      next: resp => {
        console.log('resp: ', resp);
        this.emailSettings.set(resp.data ?? {});
        this.emailEnabled.set(resp.data?.['Email_Enabled'] === 'true');
        this.loadingService.hideLoading();
      },
      error: error => {
        console.log('error: ', error);
        this.loadingService.hideLoading();
      }
    });

    this.loadJobStatuses();

    this.store.updateBreadCrumbData({
      title: 'System',
      titleIcon: 'fa-regular fa-cog fa-lg'
    });
  }

  loadJobStatuses(): void {
    this.systemApi.systemJobStatus().subscribe({
      next: resp => {
        this.jobStatusList.set(resp.data ?? []);
      },
      error: error => {
        console.error('Error loading job statuses: ', error);
      }
    });
  }

  jobStatusSeverity(status?: JobRunStatus): 'info' | 'warn' | 'danger' {
    switch (status) {
      case JobRunStatus.Running:
        return 'warn';
      case JobRunStatus.Failed:
      case JobRunStatus.Interrupted:
        return 'danger';
      default:
        return 'info';
    }
  }

  triggerTestEmail(): void {
    this.systemApi.sendTestEmail().subscribe({
      next: resp => {
        if (resp.success == true) {
          console.log('triggerTestEmail resp: ', resp);
          window.alert('Test email triggered. Check your inbox.\n' + resp.data);
        }else{
          console.log('triggerTestEmail resp: ', resp);
          window.alert('Test email failed. Check console error\n'+resp.error);
        }
      },
      error: error => {
        console.log('triggerTestEmail error: ', error);
        window.alert('Test email failed. Check console error');
      }
    })
  }

  saveEmailSettings(): void {
    this.loadingService.showLoading({message: 'Saving...', targetElement: this.host});
    const req = {
      'Email_Smtp_Host': this.emailSettings()['Email_Smtp_Host'] ?? '',
      'Email_FromAddress': this.emailSettings()['Email_FromAddress'] ?? '',
      'Email_Enabled': this.emailEnabled() + '',
      'Email_ComplianceAddress': this.emailSettings()['Email_ComplianceAddress'] ?? ''
    };

    this.systemApi.saveEmailSettings(req).subscribe({
      next: resp => {
        console.log('resp: ', resp);
        this.emailSettings.set(resp.data ?? {});
        this.emailEnabled.set(resp.data?.['Email_Enabled'] === 'true');
        this.loadingService.hideLoading();
      },
      error: error => {
        console.log('error: ', error);
        this.loadingService.hideLoading();
      }
    });
  }

  protected readonly formatDuration = formatDuration;
}
